import { useIsFocused } from '@react-navigation/native'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { getIsLoggedIn } from '../../auth/utils/getIsLoggedIn'
import { getUserQueryOptions } from '../../user/services/useUserQuery'
import { messageApi } from '../api/messageApi'
import type { TConversationApi } from '../api/types/messageApi.types'
import { useConversationContact } from './useConversationContact'
import { getConversationsPreviewOptions } from './useConversationsPreviewQuery'

export const getConversationOptions = (conversationId?: number, placeholderData?: TConversationApi) =>
  queryOptions({
    queryKey: ['conversation', conversationId],
    // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by enabled
    queryFn: ({ signal }) => messageApi.getSingleConversation(conversationId!, { signal }),
    retry: 0,
    enabled: getIsLoggedIn() && conversationId !== undefined,
    placeholderData,
  })

export const useSingleConversationQuery = (conversationId?: number) => {
  const queryClient = useQueryClient()
  const isFocused = useIsFocused()

  const placeholderData = queryClient
    .getQueryData(getConversationsPreviewOptions().queryKey)
    ?.pages.flat()
    .find(({ id }) => id === conversationId)

  const query = useQuery(getConversationOptions(isFocused ? conversationId : undefined, placeholderData))

  const contact = useConversationContact(query.data?.conversationUsers)

  useEffect(() => {
    if (contact) queryClient.setQueryData(getUserQueryOptions(contact.id).queryKey, contact)
  })

  /**
   * Update the conversation preview list to reflect that the conversation is now read
   * Once updated, useConversationsPreviewQuery will update the status
   */
  useEffect(() => {
    if (query.isSuccess) {
      const conversationsInfiniteData = queryClient.getQueryData(getConversationsPreviewOptions().queryKey)
      const conversations = conversationsInfiniteData?.pages.flat()
      const conversation = conversations?.find(({ id }) => id === conversationId)
      if (conversation?.hasUnreadMessages) {
        // When conversations and status are refetched too early, they are not updated with the new unread conversations
        const timeout = setTimeout(
          () => queryClient.invalidateQueries({ queryKey: getConversationsPreviewOptions().queryKey }),
          500,
        )
        return () => clearTimeout(timeout)
      }
    }
  }, [conversationId, query.isSuccess, queryClient])

  return query
}
